import { getProductLongDescription } from '@app/pages/i/components/ProductStyleSwitcher/useProductSwitchData';
import Box from '@redbubble/design-system/src/Box';
import * as constants from '@redbubble/design-system/src/constants';
import { Field, FieldMessage } from '@redbubble/design-system/src/Form';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import Stack from '@redbubble/design-system/src/Stack';
import { defineMessages, IntlShape, useIntl } from '@redbubble/next/intl';
import { useMemo } from 'react';
import { FAMILY_OPTION_PREFIX } from '../../../utils/ptrConfiguratorUtils';
import { LoadingBoundary } from '../../LoadingBoundary';
import {
  compareDisplayOrder,
  SHOW_INSTRUCTIONS_FOR_IPHONE_PRODUCT_TYPES,
  SHOW_INSTRUCTIONS_FOR_SAMSUNG_PRODUCT_TYPES,
} from '../configuratorFormUtils';
import { AttributeName } from '../types';
import { ErrorType, InputErrorMessage } from './InputErrorMessage';
import type {
  BasicConfiguratorOption,
  CommonAttributeConfiguratorProps,
  FieldType,
} from './types';

const messages = defineMessages({
  placeholder: { defaultMessage: 'Please select an option' },
  iPhoneFieldText: {
    defaultMessage:
      'To find your iPhone model, open the <strong>Settings</strong> app, tap <strong>General</strong>, and then tap <strong>About</strong>.',
  },
  samsungFieldText: {
    defaultMessage:
      'To find your Samsung Galaxy model, open the <strong>Settings</strong> app, and then tap <strong>About Phone</strong> or <strong>About Device</strong>.',
  },
});

/**
 * A "Simple" field only renders text data, in one of 3 ways, depending on the supplied "fieldType" prop.
 * 'select' | 'radio-selector' | 'button-toggle'
 */
export type SimpleFieldConfiguratorProps = {
  fieldType: FieldType;
  options: Array<BasicConfiguratorOption>;
  errors: Record<string, string>;
  currentProductTypeId?: string;
  isPdpStylePickerTreatment?: boolean;
} & CommonAttributeConfiguratorProps;

const getPhoneInstructionMessage = (
  intl: IntlShape,
  currentProductTypeId: string
) => {
  if (!currentProductTypeId) return null;
  if (
    SHOW_INSTRUCTIONS_FOR_IPHONE_PRODUCT_TYPES.includes(currentProductTypeId)
  ) {
    return intl.formatMessage(messages.iPhoneFieldText, {
      strong: (str) => <strong>{str}</strong>,
    });
  }

  if (
    SHOW_INSTRUCTIONS_FOR_SAMSUNG_PRODUCT_TYPES.includes(currentProductTypeId)
  ) {
    return intl.formatMessage(messages.samsungFieldText, {
      strong: (str) => <strong>{str}</strong>,
    });
  }

  return null;
};

type SimpleFieldMessageProps = {
  errors: Record<string, string>;
  attributeName: string;
  currentProductTypeId?: string;
};

const SimpleFieldMessage: React.FC<SimpleFieldMessageProps> = ({
  errors,
  attributeName,
  currentProductTypeId,
}) => {
  const intl = useIntl();
  if (errors[attributeName]) {
    return <InputErrorMessage errorType={ErrorType.Required} />;
  }
  if (attributeName === AttributeName.Model && currentProductTypeId) {
    return (
      <FieldMessage intent={constants.NEUTRAL}>
        {getPhoneInstructionMessage(intl, currentProductTypeId)}
      </FieldMessage>
    );
  }
  return null;
};

type SimpleFieldSkeletonProps = {
  actionSlot: boolean;
  attributeName: string;
  currentProductTypeId?: string;
};

const SimpleFieldSkeleton: React.FC<SimpleFieldSkeletonProps> = ({
  actionSlot,
  attributeName,
  currentProductTypeId,
}) => {
  const intl = useIntl();
  return (
    <Box marginLeft="xs" marginRight="xs" marginBottom="l">
      <Stack spacing="xs">
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          marginBottom="xs"
        >
          <Skeleton height={24} width={130} animated roundedCorners />

          {actionSlot && (
            <Skeleton height={24} width={150} animated roundedCorners />
          )}
        </Box>
        <Box>
          <Box marginBottom="xs">
            <Skeleton height={40} animated roundedCorners />
            {attributeName === AttributeName.Model && currentProductTypeId && (
              <FieldMessage intent={constants.NEUTRAL}>
                {getPhoneInstructionMessage(intl, currentProductTypeId)}
              </FieldMessage>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export const SimpleFieldConfigurator: React.FC<
  SimpleFieldConfiguratorProps
> = ({
  fieldType,
  label,
  attributeName,
  currentValue,
  options,
  errors,
  actionSlot,
  currentProductTypeId,
  isPdpStylePickerTreatment,
}) => {
  const intl = useIntl();
  const sortedOptions = useMemo(
    () =>
      options
        .sort(compareDisplayOrder)
        .map((attribute) => ({ ...attribute, label: attribute.defaultText })),
    [options]
  );

  const isFamilyOption = attributeName.startsWith(FAMILY_OPTION_PREFIX);
  const description =
    isFamilyOption && isPdpStylePickerTreatment && currentProductTypeId
      ? getProductLongDescription(intl, currentProductTypeId)
      : undefined;

  return (
    <LoadingBoundary
      loadingComponent={
        <SimpleFieldSkeleton
          actionSlot={!!actionSlot}
          attributeName={attributeName}
          currentProductTypeId={currentProductTypeId}
        />
      }
    >
      <Field
        label={label}
        description={description}
        fluid
        type={fieldType}
        multiple={false}
        intent={errors[attributeName] && constants.ERROR}
        name={attributeName}
        value={currentValue}
        options={sortedOptions}
        placeholder={intl.formatMessage(messages.placeholder)}
        actionSlot={actionSlot}
      >
        <SimpleFieldMessage
          errors={errors}
          attributeName={attributeName}
          currentProductTypeId={currentProductTypeId}
        />
      </Field>
    </LoadingBoundary>
  );
};
